import { ActionTree, MutationTree } from "vuex";
import {
  IOrder,
  IOrderRow,
  IDeliveryDetails,
  IDiscounts,
  IAvailability,
  IServiceproduct,
  BlockableActivity,
  IRootState,
  ICartState,
} from "@iloq/shared";
import { Logger } from "@iloq/shared";
import OrderingApi from "@/api/OrderingApi";
import _ from "lodash";
import i18n from "../i18n";
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";
import calculateCartSum, { calculateRowTotalSum } from "@/util/PriceUtil";
import { cloneDeep } from "lodash";

const orderingApi = new OrderingApi();
let localStorageKey = "cartState";

export enum Availability {
  AVAILABLE_ON_DATE = "AVAILABLE_ON_DATE",
  AVAILABLE = "AVAILABLE",
  OUT_OF_STOCK = "OUT_OF_STOCK",
  NOT_AVAILABLE = "NOT_AVAILABLE",
}
const initialState: ICartState = {
  cartRows: [],
  totalSum: {
    price: 0,
    currencyCode: "",
  },
  deliveryDetails: {
    companyName: "",
    receiverName: "",
    email: "",
    phoneNumber: "",
    deliveryTypeCode: "",
    additionalDetails: "",
    reference: "",
    externalOrderNumber: "",
    requestedDeliveryDate: undefined,
    shipToAddress: {
      streetAddress: "",
      streetAddress2: "",
      zipCode: "",
      city: "",
      cities: [],
      stateProvinceCode: undefined,
      country: "",
      countryCode: "",
    },
  },
  discounts: {
    projectDiscountCode: undefined,
  },
  projectIdDisabled: "",
  hydrated: false,
};

const removeOrderRow = (rows: IOrderRow[], orderRow: IOrderRow): IOrderRow[] =>
  rows.filter((row) => row.product.id !== orderRow.product.id);

const includesMatchingCategories = (categories?: string[], categoriesToCompare?: string[]) =>
  categories?.some((category) => categoriesToCompare?.includes(category));

const removeOrderRowWithSameCategory = (rows: IOrderRow[], orderRow: IOrderRow) =>
  rows.filter(
    (row) => !includesMatchingCategories(row.product.categories, orderRow.product.categories),
  );

const actions: ActionTree<IRootState, IRootState> = {
  async initializeShoppingCart({ commit, dispatch, rootState }): Promise<void> {
    Logger.debug("ShoppingCartModule: Initialize shopping cart dispatched, rootState=", rootState);
    const rootStateWithData = rootState as any;
    const defaultCartState: ICartState = {
      deliveryDetails: {
        companyName: rootStateWithData.partnerInfo.partnerInfo.partnerName,
        receiverName: `${rootStateWithData.user.firstName} ${rootStateWithData.user.lastName}`,
        email: rootStateWithData.user.email,
        phoneNumber: rootStateWithData.partnerInfo.partnerInfo.phoneNumber,
        deliveryTypeCode: "",
        additionalDetails: "",
        reference: "",
        requestedDeliveryDate: undefined,
        shipToAddress: {
          streetAddress: rootStateWithData.partnerInfo.partnerInfo.address,
          streetAddress2: rootStateWithData.partnerInfo.partnerInfo.address2,
          zipCode: rootStateWithData.partnerInfo.partnerInfo.zipCode,
          city: "",
          cities: [],
          stateProvinceCode: undefined,
          country: "",
          countryCode: rootStateWithData.partnerInfo.partnerInfo.country,
        },
      },
      cartRows: [],
      totalSum: {
        price: 0,
        currencyCode: "",
      },
      discounts: {
        projectDiscountCode: undefined,
      },
      hydrated: false,
      projectIdDisabled: "",
    };

    localStorageKey = `cartState-${(rootState as any).user.email}`;

    const savedCartStateJson = localStorage.getItem(localStorageKey);
    let savedCartState: ICartState | Record<string, unknown> = {};
    if (savedCartStateJson) {
      savedCartState = JSON.parse(savedCartStateJson) as ICartState;

      if (savedCartState?.deliveryDetails?.requestedDeliveryDate) {
        // Convert serialized date string to Date
        savedCartState.deliveryDetails.requestedDeliveryDate = new Date(
          savedCartState.deliveryDetails.requestedDeliveryDate,
        );
      }

      // fetch products with fresh data and prices
      savedCartState.cartRows = await dispatch(
        "productSearch/ensureProductsExistWithLatestDataAndPrice",
        { rows: savedCartState.cartRows, failWhenNoProducts: false },
        { root: true },
      );

      // fetch up-to-date discounts from API
      if (savedCartState.discounts?.projectDiscountCode) {
        const discounts: IDiscounts | undefined = await dispatch(
          "project/fetchDiscounts",
          savedCartState.discounts.projectDiscountCode,
          { root: true },
        );

        if (discounts) {
          savedCartState.discounts = discounts;
        }
      }
      Logger.debug(
        "Found existing cartState from localStorage, using it initialize form",
        savedCartState,
      );
    } else {
      Logger.debug("ShoppingCartModule: no existing orders found, using values from root state");
    }
    commit("initializeCart", _.merge(defaultCartState, savedCartState));
    commit("calculateSums");
  },
  async addToCart({ commit, dispatch, rootGetters }, orderRow: IOrderRow): Promise<boolean> {
    if (
      await dispatch(
        "partnerInfo/handleBlockedActivity",
        { anyOf: [BlockableActivity.SHIPMENTS, BlockableActivity.INVOICING] },
        { root: true },
      )
    ) {
      return false;
    }

    const orderingAllowed = rootGetters["orderingPermission/isOrderingAllowed"](
      orderRow.product.family,
    );
    if (orderingAllowed) {
      commit("addProductToCart", orderRow);
      commit("calculateSums");
      commit("saveToLocalStorage");
      return true;
    } else {
      commit(
        "showGeneralError",
        {
          dialogTitle: i18n.t("shoppingCart.orderingNotAllowedDialog.title"),
          dialogText: i18n.t("shoppingCart.orderingNotAllowedDialog.body"),
        },
        {
          root: true,
        },
      );
      return false;
    }
  },
  removeFromCart({ commit, rootState }, orderRow: IOrderRow): void {
    commit("removeFromCart", {
      orderRow,
      serviceproductIds: (rootState as any).productData.serviceproducts.ids,
    });
    commit("calculateSums");
    commit("saveToLocalStorage");
  },
  calculateSums({ commit }): void {
    commit("calculateSums");
    commit("saveToLocalStorage");
  },
  setAvailabilities({ commit }, availabilityResults: IAvailability): void {
    commit("setAvailabilities", availabilityResults);
    commit("saveToLocalStorage");
  },
  setDeliveryDetails({ commit }, deliveryDetails: IDeliveryDetails): void {
    commit("setDeliveryDetails", deliveryDetails);
    commit("saveToLocalStorage");
  },
  setDiscounts({ commit }, discounts: IDiscounts): void {
    commit("setDiscounts", discounts);
    commit("calculateSums");
    commit("saveToLocalStorage");
  },
  setProjectIdDisabled({ commit }, projectIdDisabled: string): void {
    commit("setProjectIdDisabled", projectIdDisabled);
    commit("saveToLocalStorage");
  },
  unsetDiscounts({ commit }): void {
    commit("unsetDiscounts");
    commit("calculateSums");
    commit("saveToLocalStorage");
  },
  async sendOrder({ dispatch }, order: IOrder): Promise<IOrder | void> {
    if (
      await dispatch(
        "partnerInfo/handleBlockedActivity",
        { anyOf: [BlockableActivity.SHIPMENTS, BlockableActivity.INVOICING] },
        { root: true },
      )
    ) {
      throw new OrderingBlockedError();
    }

    return orderingApi
      .sendOrder(order)
      .then((data: IOrder) => {
        localStorage.removeItem(localStorageKey);
        return Promise.resolve(data);
      })
      .catch(handleCommonApiErrors);
  },
  storeOrderAsDraft({ commit }, order: IOrder): Promise<IOrder | void> {
    Logger.debug(`Store order as draft, order=${order}`);
    commit("saveToLocalStorage");
    return orderingApi
      .storeOrderAsDraft(order)
      .then((data: IOrder) => Promise.resolve(data))
      .catch(handleCommonApiErrors);
  },
  async continueOrderFromDraft({ commit, dispatch }, orderToClone: IOrder): Promise<void> {
    const order = cloneDeep(orderToClone);
    order.deliveryDetails.requestedDeliveryDate = undefined;
    if (!order.deliveryDetails.shipToAddress.cities)
      order.deliveryDetails.shipToAddress.cities = [];

    const discounts: IDiscounts = await dispatch(
      "project/fetchDiscounts",
      order.discounts?.projectDiscountCode,
      { root: true },
    );

    const draftCartState: ICartState = {
      deliveryDetails: order.deliveryDetails,
      cartRows: order.rows,
      totalSum: order.totalSum,
      discounts,
      hydrated: true,
      projectIdDisabled: order.projectIdDisabled,
    };
    commit("initializeCart", draftCartState);
    commit("calculateSums");
    commit("saveToLocalStorage", draftCartState);
  },
  removeServiceproduct({ commit }, product: IServiceproduct): void {
    commit("removeServiceproduct", { product });
    commit("saveToLocalStorage");
  },
  addOrReplaceServiceproduct({ commit }, row: IOrderRow): void {
    commit("addOrReplaceServiceproduct", row);
    commit("saveToLocalStorage");
  },
  toggleServiceproduct({ commit }, { row, checked }: { row: IOrderRow; checked: boolean }): void {
    commit("toggleServiceproduct", {
      row,
      checked,
    });
    commit("saveToLocalStorage");
  },
};

const mutations: MutationTree<ICartState> = {
  addProductToCart(state: ICartState, orderRow: IOrderRow): void {
    orderRow.rowTotalSum = orderRow.quantity * orderRow.product.price!.price;

    const originalRowsCopied = [...state.cartRows];
    const existingRow = originalRowsCopied.find(
      (row: IOrderRow) => row.product.id === orderRow.product.id,
    );
    if (existingRow !== undefined) {
      existingRow.quantity += orderRow.quantity;
      existingRow.rowTotalSum = calculateRowTotalSum(existingRow, state.discounts);
    } else {
      orderRow.rowTotalSum = calculateRowTotalSum(orderRow, state.discounts);
      originalRowsCopied.push(orderRow);
    }
    const totalSum: number = originalRowsCopied.reduce(
      (accumulator, currentValue) => accumulator + currentValue.rowTotalSum,
      0,
    );
    state.totalSum.price = totalSum;

    state.cartRows = originalRowsCopied;
  },
  removeFromCart(
    state: ICartState,
    { orderRow, serviceproductIds }: { orderRow: IOrderRow; serviceproductIds: string[] },
  ): void {
    const originalRowsCopied = [...state.cartRows];
    const arrayItemRemoved = originalRowsCopied.filter(
      (row) => row.product.id !== orderRow.product.id,
    );
    if (
      arrayItemRemoved.filter((item: IOrderRow) => !serviceproductIds.includes(item.product?.id))
        .length === 0
    ) {
      state.cartRows = [];
    } else {
      state.cartRows = arrayItemRemoved;
    }
  },
  calculateSums(state: ICartState) {
    // calculateCartSum mutates state rows directly
    const { totalSum } = calculateCartSum(state.cartRows, state.discounts);
    state.totalSum.price = totalSum.price;
    if (totalSum.currencyCode) {
      state.totalSum.currencyCode = totalSum.currencyCode;
    }
  },
  setAvailabilities(state: ICartState, availabilityResults: Array<string>) {
    state.cartRows.forEach((row: IOrderRow) => {
      let requestedDeliveryDate: Date;
      if (state.deliveryDetails.requestedDeliveryDate) {
        requestedDeliveryDate = state.deliveryDetails.requestedDeliveryDate;
      } else {
        requestedDeliveryDate = new Date();
      }
      if (availabilityResults) {
        availabilityResults.forEach((result: any) => {
          if (result.productId === row.product.id) {
            row.availabilityQty = result.quantityAvailable;
            row.availabilityDate = result.quantityAvailableDate;
          }
        });
      }
      if (!row.availabilityDate) {
        row.availability = Availability.NOT_AVAILABLE;
      } else if (
        Date.parse(row.availabilityDate.toString()) > Date.parse(requestedDeliveryDate.toString())
      ) {
        row.availability = Availability.AVAILABLE_ON_DATE;
      } else if (row.availabilityQty >= row.quantity) {
        row.availability = Availability.AVAILABLE;
      } else if (row.availabilityQty === 0) {
        row.availability = Availability.OUT_OF_STOCK;
      } else {
        row.availability = Availability.NOT_AVAILABLE;
      }
    });
  },

  saveToLocalStorage(state: ICartState) {
    localStorage.setItem(localStorageKey, JSON.stringify(state));
  },
  removeFromLocalStorage() {
    localStorage.removeItem(localStorageKey);
  },
  setDeliveryDetails(state: ICartState, deliveryDetails: IDeliveryDetails): void {
    state.deliveryDetails = deliveryDetails;
  },
  setDiscounts(state: ICartState, discounts: IDiscounts): void {
    state.discounts = discounts;
  },
  unsetDiscounts(state: ICartState): void {
    state.discounts = {
      projectDiscountCode: undefined,
    };
  },
  setProjectIdDisabled(state: ICartState, projectIdDisabled: string): void {
    state.projectIdDisabled = projectIdDisabled;
  },
  initializeCart(state: ICartState, data: any): void {
    state.deliveryDetails = data.deliveryDetails;
    state.cartRows = data.cartRows;
    state.totalSum = data.totalSum;
    state.discounts = data.discounts;
    state.hydrated = true;
    state.projectIdDisabled = data.projectIdDisabled;
  },
  removeServiceproduct(state, orderRow: IOrderRow): void {
    state.cartRows = removeOrderRowWithSameCategory(state.cartRows, orderRow);
  },
  addOrReplaceServiceproduct(state, orderRow: IOrderRow): void {
    state.cartRows = removeOrderRowWithSameCategory(state.cartRows, orderRow).concat(orderRow);
  },
  toggleServiceproduct(
    state,
    { row: orderRow, checked }: { row: IOrderRow; checked: boolean },
  ): void {
    if (checked) {
      state.cartRows = removeOrderRow(state.cartRows, orderRow).concat(orderRow);
    } else {
      state.cartRows = removeOrderRow(state.cartRows, orderRow);
    }
  },
};

export const shoppingCartModule = {
  actions,
  mutations,
  state: initialState,
  namespaced: true,
};

export class OrderingBlockedError extends Error {
  constructor() {
    super("Ordering is blocked");
    Object.setPrototypeOf(this, OrderingBlockedError.prototype);
  }
}
